import { Check, ArrowRight, ArrowLeft } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHead, Section } from "@/components/Shared";
import { UI } from "@/i18n/ui";
import { WHATSFORM_URL } from "@/data/site";
import {
  Stepper, StepService, StepPsych, StepSchedule, StepContact, StepSummary,
  SuccessScreen, buildWaText, useBookingForm,
} from "@/pages/booking-steps";

const META = {
  id: { title: "Buat Janji Konsultasi Psikolog di Denpasar | Bali Empower", desc: "Buat janji konsultasi dengan psikolog Bali Empower di Denpasar dalam waktu kurang dari 2 menit. Pilih layanan, jadwal yang diinginkan, dan tim kami akan mengonfirmasi via WhatsApp." },
  en: { title: "Book a Psychology Consultation in Denpasar | Bali Empower", desc: "Book a consultation with a Bali Empower psychologist in Denpasar in under 2 minutes. Choose your service and preferred schedule; our team confirms via WhatsApp." },
};

const WhatsformNote = ({ lang }) => (
  <p className="mb-8 -mt-4 text-sm text-[#4B5563]">
    {lang === "id" ? "Lebih suka formulir WhatsApp resmi kami? " : "Prefer our official WhatsApp form? "}
    <a href={WHATSFORM_URL} target="_blank" rel="noopener noreferrer" data-testid="booking-whatsform-link"
      className="font-semibold text-[#4A6B53] hover:underline">
      {lang === "id" ? "Buka WhatsForm" : "Open WhatsForm"}
    </a>
  </p>
);

const StepNav = ({ step, canNext, sending, m, onBack, onNext, onSubmit }) => (
  <div className="mt-10 flex justify-between">
    {step > 0 ? (
      <button onClick={onBack} data-testid="booking-back-btn"
        className="inline-flex items-center gap-2 rounded-full border border-[#E5E1D8] px-6 py-3 text-sm font-medium text-[#1F2937] hover:border-[#4A6B53] transition-colors">
        <ArrowLeft size={15} /> {m.back}
      </button>
    ) : <span />}
    {step < 4 ? (
      <button onClick={onNext} disabled={!canNext} data-testid="booking-next-btn"
        className="inline-flex items-center gap-2 rounded-full bg-[#4A6B53] text-white px-8 py-3 text-sm font-semibold hover:bg-[#3A5542] transition-colors disabled:opacity-40">
        {m.next} <ArrowRight size={15} />
      </button>
    ) : (
      <button onClick={onSubmit} disabled={sending} data-testid="booking-confirm-btn"
        className="inline-flex items-center gap-2 rounded-full bg-[#4A6B53] text-white px-8 py-3 text-sm font-semibold hover:bg-[#3A5542] transition-colors disabled:opacity-60">
        {sending ? "..." : m.confirmBooking} <Check size={15} />
      </button>
    )}
  </div>
);

export default function Booking({ lang }) {
  const t = UI[lang];
  const m = t.misc;
  const form = useBookingForm(lang);
  const meta = META[lang];
  const steps = [m.stepService, m.stepPsych, m.stepSchedule, m.stepContact, m.stepConfirm];

  if (form.done) return <SuccessScreen lang={lang} m={m} meta={meta} waText={buildWaText(lang, form.data)} />;

  const stepBody = [
    <StepService key="s0" lang={lang} data={form.data} setField={form.setField} />,
    <StepPsych key="s1" lang={lang} m={m} data={form.data} setField={form.setField} />,
    <StepSchedule key="s2" m={m} data={form.data} setField={form.setField} />,
    <StepContact key="s3" m={m} data={form.data} setField={form.setField} />,
    <StepSummary key="s4" m={m} data={form.data} />,
  ][form.step];

  return (
    <div data-testid="booking-page">
      <Seo title={meta.title} description={meta.desc} lang={lang} />
      <PageHead eyebrow={t.cta.book} title={m.bookingTitle} sub={m.bookingSub} />
      <Section className="pt-0">
        <div className="max-w-2xl">
          <WhatsformNote lang={lang} />
          <Stepper steps={steps} step={form.step} />
          <h2 className="text-2xl font-display text-[#1F2937] mb-6">{steps[form.step]}</h2>
          {stepBody}
          <StepNav step={form.step} canNext={form.canNext} sending={form.sending} m={m}
            onBack={form.goBack} onNext={form.goNext} onSubmit={form.submit} />
        </div>
      </Section>
    </div>
  );
}
